import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Clock,
  Calendar,
  Stethoscope,
  CreditCard,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/hooks';
import { notificationsApi, NotificationItem } from '@/api/notifications-api';

export function NotificationBell() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const { data: notifications = [], isLoading } = useQuery<NotificationItem[]>({
    queryKey: ['notifications', 'bell', user?.id],
    queryFn: async () => {
      const res = await notificationsApi.getNotifications({ limit: 10 });
      return res?.items ?? [];
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  const { data: unreadCount = 0 } = useQuery<number>({
    queryKey: ['notifications', 'bell', 'unread-count', user?.id],
    queryFn: async () => {
      const res = await notificationsApi.getNotifications({ limit: 1, status: 'UNREAD' });
      return res?.total ?? 0;
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      return notificationsApi.markAllAsRead();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['patient', 'notifications'] });
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return notificationsApi.markAsRead(notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['patient', 'notifications'] });
    },
  });

  const formatTime = (dateStr?: string | null) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Agora';
      if (diffMins < 60) return `${diffMins}m atrás`;
      if (diffHours < 24) return `${diffHours}h atrás`;
      if (diffDays === 1) return 'Ontem';
      if (diffDays < 7) return `${diffDays}d atrás`;
      return date.toLocaleDateString('pt-BR');
    } catch {
      return '';
    }
  };

  const getIcon = (item: NotificationItem) => {
    const titleLower = item.title?.toLowerCase() || '';
    const data = (item.data as Record<string, any>) || {};
    const type = (data.type as string)?.toLowerCase() || '';

    if (
      data.appointmentId ||
      data.sessionId ||
      data.expressId ||
      titleLower.includes('consulta') ||
      titleLower.includes('agendamento')
    ) {
      return <Calendar className="h-4 w-4 text-primary shrink-0" />;
    }
    if (
      data.prescriptionId ||
      titleLower.includes('receita') ||
      titleLower.includes('atestado') ||
      titleLower.includes('exame')
    ) {
      return <Stethoscope className="h-4 w-4 text-indigo-500 shrink-0" />;
    }
    if (
      titleLower.includes('pagamento') ||
      titleLower.includes('pix') ||
      titleLower.includes('saque') ||
      type.includes('payment')
    ) {
      return <CreditCard className="h-4 w-4 text-emerald-500 shrink-0" />;
    }
    if (
      titleLower.includes('indique') ||
      titleLower.includes('parceiro') ||
      titleLower.includes('comissão')
    ) {
      return <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />;
    }
    return <Bell className="h-4 w-4 text-blue-500 shrink-0" />;
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    const isUnread = notification.status !== 'READ' && !notification.readAt;
    if (isUnread) {
      markReadMutation.mutate(notification.id);
    }

    setIsOpen(false);

    const data = (notification.data as Record<string, any>) || {};
    const isDoctor = user?.role === 'DOCTOR';

    if (data.sessionId) {
      navigate(`/telemedicine/video/${data.sessionId}`);
      return;
    }

    if (data.expressId) {
      if (data.sessionId) {
        navigate(`/telemedicine/video/${data.sessionId}`);
      } else if (data.appointmentId) {
        navigate(`/telemedicine/waiting/${data.appointmentId}`);
      } else {
        navigate(`/telemedicine/waiting/${data.expressId}`);
      }
      return;
    }

    if (data.appointmentId) {
      if (isDoctor) {
        navigate('/medico/agenda');
      } else {
        navigate(`/agendamentos/${data.appointmentId}`);
      }
      return;
    }

    if (data.prescriptionId) {
      if (isDoctor) {
        navigate('/medico/receitas');
      } else {
        navigate('/receitas');
      }
      return;
    }

    const url = data.url || data.actionUrl || data.link;
    if (url) {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.location.href = url;
      } else {
        navigate(url);
      }
      return;
    }

    navigate('/notificacoes');
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notificacoes');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-all cursor-pointer border border-transparent hover:border-border/60"
        aria-label="Notificações"
        title="Notificações"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            data-testid="unread-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center shadow-xs animate-in zoom-in"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            data-testid="notification-backdrop"
          />
          <div
            data-testid="notification-dropdown"
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-card text-card-foreground rounded-2xl shadow-2xl border border-border z-50 overflow-hidden dark:bg-neutral-900 dark:border-neutral-800 animate-in fade-in slide-in-from-top-2 duration-150"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/80 bg-muted/40 dark:bg-neutral-900/90">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm text-foreground">Notificações</h3>
                {unreadCount > 0 && (
                  <span className="text-[11px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {unreadCount} {unreadCount === 1 ? 'nova' : 'novas'}
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllReadMutation.mutate()}
                  disabled={markAllReadMutation.isPending}
                  className="text-xs text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  <span>Marcar todas como lidas</span>
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto divide-y divide-border/40">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                  <Loader2 className="animate-spin h-6 w-6 text-primary" />
                  <span className="text-xs">Carregando notificações...</span>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-10 text-center px-4">
                  <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mx-auto mb-3 text-muted-foreground/60">
                    <Bell className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    Nenhuma notificação por enquanto
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Você será avisado sobre novidades e agendamentos.
                  </p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const isUnread = notification.status !== 'READ' && !notification.readAt;
                  return (
                    <div
                      key={notification.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleNotificationClick(notification)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleNotificationClick(notification);
                        }
                      }}
                      className={`px-4 py-3.5 flex items-start gap-3 transition-colors cursor-pointer select-none ${
                        isUnread
                          ? 'bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15'
                          : 'hover:bg-muted/60 dark:hover:bg-neutral-800/60'
                      }`}
                    >
                      <div className="mt-0.5 p-2 rounded-xl bg-background border border-border/60 shadow-2xs">
                        {getIcon(notification)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`text-xs sm:text-sm truncate ${
                              isUnread
                                ? 'font-semibold text-foreground'
                                : 'font-normal text-foreground/80'
                            }`}
                          >
                            {notification.title}
                          </p>
                          {isUnread && (
                            <span
                              data-testid="unread-dot"
                              className="w-2 h-2 rounded-full bg-blue-500 ring-2 ring-blue-500/20 shrink-0"
                            />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                          {notification.message}
                        </p>
                        {notification.createdAt && (
                          <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground/80">
                            <Clock className="h-3 w-3" />
                            <span>{formatTime(notification.createdAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="px-4 py-3 border-t border-border/80 bg-muted/40 dark:bg-neutral-900/90">
              <button
                onClick={handleViewAll}
                className="w-full text-center text-xs font-semibold text-primary hover:text-primary/80 transition-colors py-1 cursor-pointer"
              >
                Ver todas as notificações
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
